import { useState } from 'react';
import { Button, Divider, Tooltip, makeStyles } from '@fluentui/react-components';
import { ArrowDownload20Regular, MailRegular, ReceiptRegular } from '@fluentui/react-icons';
import { PDFViewer, PDFDownloadLink, pdf } from '@react-pdf/renderer';
import { BrandCard, Column, PortalSlotContent, Row } from '../../../common';
import { Invoice, Case, Account, CaseEngagementDoc, Contact } from '../../../../data/types';
import { InvoiceDoc } from './InvoiceDoc';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { useLocation, useNavigate } from 'react-router';
import { InvoiceTabs } from './InvoiceEnums';
import { EmailInvoiceDialog } from './EmailInvoiceDialog';
import { useToasts } from '../../../toasts/useToasts';

const useStyles = makeStyles({
    downloadBtn: {
        fontSize: '13px',
        paddingBlockStart: '0',
        paddingBlockEnd: '0',
        ':focus': {
            outline: 'none',
            boxShadow: 'none',
        },
    },
    container: {
        height: '100%',
        padding: '.5rem',
    },
    pdfContainer: {
        height: '100%',
        width: '100%',
        border: '1px solid #e1e1e1',
        borderRadius: '8px',
    },
});

export type InvoicePDFViewerPageProps = {
    invoice: Invoice;
    account?: Account;
    engagementDoc?: CaseEngagementDoc;
};

export const InvoicePDFViewerPage = (props: InvoicePDFViewerPageProps) => {
    const { invoice, account, engagementDoc } = props;

    const { caseForm, invoiceService } = useCaseServicesContext()!;
    const navTo = useNavigate();
    const { pathname, state } = useLocation();
    const toasts = useToasts();

    const caseObj = caseForm.values as Case;
    const clientContacts = caseObj.retainedBy?.contacts || [];

    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();

    //Logic
    const getFileName = () => {
        return `Invoice_${invoice.invoiceNumber}_${caseObj.caseName?.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
    };
    const navToInvoiceList = () => {
        navTo(pathname, { state: { ...state, subTab: InvoiceTabs.invoiceList } });
    };
    const emailInvoice = async (contact: Contact) => {
        if (!invoice.invoiceId || !contact.contactId) return;

        setSendingEmail(true);
        try {
            const invoicePdf = await pdf(
                <InvoiceDoc
                    invoice={invoice}
                    caseObj={caseObj}
                    account={account!}
                    engagementDoc={engagementDoc}
                />,
            ).toBlob();

            await invoiceService.emailInvoiceAsync(invoice.invoiceId, contact.contactId, invoicePdf, getFileName());
            toasts.invoices.notifyInvoiceEmailed(invoice, contact);
            setEmailDialogOpen(false);
        } catch {
            toasts.invoices.notifyInvoiceEmailError(invoice);
        } finally {
            setSendingEmail(false);
        }
    };

    return (
        <>
            <PortalSlotContent name='invoicesPageHeader'>
                <Row flex='0 0 auto' gap='medium'>
                    <Column flex='0 0 auto' padding='pr-1'>
                        <Button className={commonStyles.button.root} icon={<ReceiptRegular />} onClick={navToInvoiceList}>
                            Invoice List
                        </Button>
                    </Column>
                    <Divider vertical />
                    <Column flex='0 0 auto'>
                        <PDFDownloadLink
                            document={
                                <InvoiceDoc
                                    invoice={invoice}
                                    caseObj={caseObj}
                                    account={account!}
                                    engagementDoc={engagementDoc!}
                                />
                            }
                            fileName={getFileName()}
                        >
                            {({ loading }) => (
                                <Button
                                    className={commonStyles.button.root}
                                    icon={<ArrowDownload20Regular />}
                                    appearance='secondary'
                                    disabled={loading}
                                    data-testid='invoice-pdf-download-button'
                                >
                                    {loading ? 'Generating...' : 'Download PDF'}
                                </Button>
                            )}
                        </PDFDownloadLink>
                    </Column>
                    <Column flex='0 0 auto'>
                        <Tooltip
                            content={clientContacts.length ? 'Email invoice PDF' : 'Add a client contact before emailing an invoice'}
                            relationship='label'
                        >
                            <span>
                                <Button
                                    className={commonStyles.button.root}
                                    icon={<MailRegular />}
                                    appearance='secondary'
                                    disabled={!clientContacts.length || sendingEmail}
                                    onClick={() => setEmailDialogOpen(true)}
                                    data-testid='invoice-pdf-email-button'
                                >
                                    Email Invoice
                                </Button>
                            </span>
                        </Tooltip>
                    </Column>
                </Row>
            </PortalSlotContent>
            <Column className={styles.container}>
                <div className={styles.pdfContainer}>
                    <PDFViewer style={{ height: '100%', width: '100%' }}>
                        <InvoiceDoc
                            invoice={invoice}
                            caseObj={caseObj}
                            account={account!}
                            engagementDoc={engagementDoc}
                        />
                    </PDFViewer>
                </div>
            </Column>
            <EmailInvoiceDialog
                open={emailDialogOpen}
                contacts={clientContacts}
                invoiceNumber={invoice.invoiceNumber}
                sending={sendingEmail}
                onOpenChange={setEmailDialogOpen}
                onSend={emailInvoice}
            />
        </>
    );
};
